import { z } from 'zod';
import { draftInputSchema } from './drafts.ts';
import type { PublishedGame } from './publications.ts';

export const publicationEditInputSchema = z.strictObject({
  revision: z.number().int().min(1),
  title: z.string().trim().min(3).max(100),
  questions: z.array(z.strictObject({
    prompt: z.string().trim().max(240),
    choices: z.array(z.strictObject({
      text: z.string().trim().max(100),
      isCorrect: z.boolean(),
    })).min(2).max(6),
  })).min(1).max(25),
});
export type PublicationEditInput = z.infer<typeof publicationEditInputSchema>;

export class PublicationEditError extends Error {
  readonly code: 'PUBLICATION_CONFLICT' | 'PUBLICATION_STRUCTURE_CHANGED';
  constructor(code: PublicationEditError['code'], message: string) {
    super(message);
    this.code = code;
  }
}

export function publicationEditOf(game: PublishedGame): PublicationEditInput {
  return {
    revision: game.revision ?? 1,
    title: game.title,
    questions: game.questions.map(q => ({
      prompt: q.prompt,
      choices: q.choices.map(c => ({ text: c.text, isCorrect: c.isCorrect })),
    })),
  };
}

/** Only editable text/answer keys cross this boundary. Media, IDs and rules stay server-owned. */
export function applyPublicationEdit(game: PublishedGame, raw: PublicationEditInput): PublishedGame {
  const edit = publicationEditInputSchema.parse(raw);
  if (edit.revision !== (game.revision ?? 1)) {
    throw new PublicationEditError('PUBLICATION_CONFLICT', 'Bu yayın başka bir işlemde güncellendi. Güncel yayını açıp değişikliklerini yeniden uygula.');
  }
  if (edit.questions.length !== game.questions.length
    || edit.questions.some((q, i) => q.choices.length !== game.questions[i].choices.length)) {
    throw new PublicationEditError('PUBLICATION_STRUCTURE_CHANGED', 'Yayındaki soru ve seçenek sayısı değiştirilemez.');
  }
  const next = {
    ...game,
    title: edit.title,
    questions: game.questions.map((q, i) => ({ ...q, prompt: edit.questions[i].prompt,
      choices: q.choices.map((c, j) => ({ ...c, ...edit.questions[i].choices[j] })),
    })),
  };
  draftInputSchema.parse({ ...next, levelMode: 'manual' });
  return next;
}
